import React from 'react'
import defaultClasses from './image.module.scss'
import { arrayOf, bool, oneOf, shape, string } from 'prop-types'
import resolveLinkProps from '../../resolveLinkProps'
import { mergeClasses } from '../../classify'
import Link from 'next/link'
import { makeOptimizedUrl as resourceUrl } from '../../makeUrl'
import NextImage from '@corratech/pylot-image'
import { getFastlyUrl } from '@corratech/pylot-image'
import { useSSRSafeMobile } from '@corratech/pylot-utils'

const SourceFragment = ({
    mobileImage,
    fastlyOptimizeQuality,
    imageQuality
}) => {
    if (!mobileImage) return null

    return (
        <source
            media="(max-width: 767px)"
            // TODO: fix the logic with resourceURL.
            // Right now it is not used as intended and probably should be removed, as
            // one of the options is to use next image component at the end, which do not require
            // fastly-friendly url
            srcSet={resourceUrl(mobileImage, {
                type: 'image-wysiwyg',
                quality: imageQuality,
                optimizeQuality: fastlyOptimizeQuality
            })}
        />
    )
}

const PicturePB = (props) => {
    const {
        width,
        height,
        resourceSrc,
        classes,
        altText,
        title,
        imageStyles,
        isLazyLoad,
        imageQuality,
        linkedCategory,
        imageNavCallback
    } = props

    const imageDimensions = { width, height }
    // TODO: think about the flag when to load image
    // with the layout fill

    // if both width and height are numbers
    if (width && height) {
        return (
            <NextImage
                src={resourceSrc}
                className={classes.img}
                alt={altText || title || ''}
                onClick={() => {
                    if (imageNavCallback) {
                        imageNavCallback(linkedCategory)
                    }
                }}
                style={imageStyles}
                quality={imageQuality}
                {...imageDimensions}
            />
        )
    }

    const resourceSrcOptimized = getFastlyUrl({
        src: resourceSrc,
        quality: imageQuality
    })

    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
    return (
        <img
            src={resourceSrcOptimized}
            className={classes.img}
            alt={altText || title || ''}
            loading={isLazyLoad ? 'lazy' : 'eager'} // https://web.dev/browser-level-image-lazy-loading/
            onClick={() => {
                if (imageNavCallback) {
                    imageNavCallback(linkedCategory)
                }
            }}
            style={imageStyles}
            {...(width && { width })}
            {...(height && { height })}
        />
    )
}

/**
 * Page Builder Image component.
 *
 * This component is part of the Page Builder / PWA integration. It can be consumed without Page Builder.
 *
 * @typedef Image
 * @kind functional component
 *
 * @param {props} props React component props
 *
 * @returns {React.Element} A React component that displays an Image.
 */
const Image = (props) => {
    const classes = mergeClasses(defaultClasses, props.classes)
    const classFinder = props.cssClasses?.map((cssClass, i) => {
        if (cssClass.includes('category-link')) {
            return i
        } else {
            return null
        }
    })
    const linkedCategory = props.cssClasses?.[classFinder]
    const {
        desktopImage,
        mobileImage,
        altText,
        title,
        link,
        linkType,
        openInNewTab,
        caption,
        textAlign,
        border,
        borderColor,
        borderWidth,
        borderRadius,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        imageQuality,
        cssClasses = [],
        globalOptions,
        isLazyLoad,
        imgWidth,
        imgHeight,
        imgWidthMob,
        imgHeightMob,
        imageNavCallback
    } = props

    const mobileBreakpoint = globalOptions?.viewport?.mobile ?? 767
    const isMobile = useSSRSafeMobile(mobileBreakpoint)

    const figureStyles = {
        textAlign,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft
    }
    const imageStyles = {
        border,
        borderColor,
        borderWidth,
        borderRadius
    }

    // Don't render anything if there is no image to be rendered
    if (!desktopImage && !mobileImage) {
        return null
    }

    const resourceSrc = resourceUrl(desktopImage, {
        type: 'image-wysiwyg',
        quality: imageQuality,
        optimizeQuality: (globalOptions || {}).fastlyOptimizeQuality
    })

    const PictureWithWrapper = () => {
        const width = isMobile ? imgWidthMob : imgWidth
        const height = isMobile ? imgHeightMob : imgHeight

        const mobileUrl = mobileImage
            ? getFastlyUrl({ src: mobileImage, quality: imageQuality })
            : getFastlyUrl({ src: desktopImage, quality: imageQuality })

        const isLazyLoadHtmlImg = cssClasses.includes('lazy-load')

        return (
            <>
                <picture>
                    {/* TODO: start utilizing this component if it brings any benefit. */}
                    <SourceFragment
                        mobileImage={mobileUrl}
                        imageQuality={imageQuality}
                        fastlyOptimizeQuality={
                            (globalOptions || {}).fastlyOptimizeQuality
                        }
                    />
                    <PicturePB
                        width={width}
                        height={height}
                        resourceSrc={resourceSrc}
                        classes={classes}
                        altText={altText}
                        title={title}
                        imageStyles={imageStyles}
                        isLazyLoad={isLazyLoadHtmlImg}
                        imageQuality={imageQuality}
                        linkedCategory={linkedCategory}
                        imageNavCallback={imageNavCallback}
                    />
                </picture>
                {caption && <figcaption>{caption}</figcaption>}
            </>
        )
    }

    if (typeof link === 'string') {
        const linkProps = resolveLinkProps(link, linkType)

        const homePageDynamicClass = cssClasses.find((cls) =>
            cls.startsWith('homeSection-')
        )

        const sectionName = homePageDynamicClass
            ? homePageDynamicClass.replace('homeSection-', '')
            : null

        const bannerHeroDynamicClass = cssClasses.find((cls) =>
            cls.startsWith('homeAction-')
        )

        const clickName = bannerHeroDynamicClass
            ? bannerHeroDynamicClass.replace('homeAction-', '')
            : null

        const dataAttributes = {
            ...(sectionName ? { 'data-click-section': sectionName } : {}),
            ...(clickName ? { 'data-click-name': clickName } : {})
        }

        return (
            <figure style={figureStyles} className={cssClasses.join(' ')}>
                {linkProps.to ? (
                    <Link
                        {...linkProps}
                        {...dataAttributes}
                        {...(openInNewTab ? { target: '_blank' } : {})}
                    >
                        <PictureWithWrapper />
                    </Link>
                ) : (
                    <a
                        {...linkProps}
                        {...dataAttributes}
                        {...(openInNewTab ? { target: '_blank' } : {})}
                    >
                        <PictureWithWrapper />
                    </a>
                )}
            </figure>
        )
    }

    return (
        <figure style={figureStyles} className={cssClasses.join(' ')}>
            <PictureWithWrapper />
        </figure>
    )
}

/**
 * Props for {@link Image}
 *
 * @typedef props
 *
 * @property {Object} classes An object containing the class names for the Image
 * @property {String} classes.img CSS classes for the img element
 * @property {String} desktopImage URL src of the desktop image
 * @property {String} mobileImage URL src of the mobile image
 * @property {String} altText Alternate text
 * @property {String} title Title of the image
 * @property {String} link URL to redirect to
 * @property {String} linkType Type of link
 * @property {bool} openInNewTab Flag to indicate if link should be opened in a new tab
 * @property {String} caption Caption for the image
 * @property {String} textAlign Alignment of the divider within the parent container
 * @property {String} border CSS border property
 * @property {String} borderColor CSS border color property
 * @property {String} borderWidth CSS border width property
 * @property {String} borderRadius CSS border radius property
 * @property {String} marginTop CSS margin top property
 * @property {String} marginRight CSS margin right property
 * @property {String} marginBottom CSS margin bottom property
 * @property {String} marginLeft CSS margin left property
 * @property {String} paddingTop CSS padding top property
 * @property {String} paddingRight CSS padding right property
 * @property {String} paddingBottom CSS padding bottom property
 * @property {String} paddingLeft CSS padding left property
 * @property {Array} cssClasses List of CSS classes to be applied to the component
 */
Image.propTypes = {
    classes: shape({
        img: string
    }),
    desktopImage: string,
    mobileImage: string,
    altText: string,
    title: string,
    link: string,
    linkType: oneOf(['default', 'category', 'product', 'page']),
    openInNewTab: bool,
    caption: string,
    textAlign: string,
    border: string,
    borderColor: string,
    borderWidth: string,
    borderRadius: string,
    marginTop: string,
    marginRight: string,
    marginBottom: string,
    marginLeft: string,
    paddingTop: string,
    paddingRight: string,
    paddingBottom: string,
    cssClasses: arrayOf(string)
}

export default Image
